import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  styled, 
  Paper, 
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Grid,
  Fade,
  CircularProgress,
  Alert,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getCompanyInfo, getQrMenusMap } from '../../services/migration/companyDataService';
import { getScreenPermissionsByType } from '../../services/migration/personnelDataService';
import { nodeApi } from '../../config/apiClient';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Html5Qrcode } from 'html5-qrcode';
import { QRCodeSVG } from 'qrcode.react';
import PersonnelProfileModal from './components/PersonnelProfileModal';
import PersonnelSidebar from './components/PersonnelSidebar';
import NormalTasksComponent from './components/NormalTasksComponent';
import TeamTasksComponent from './components/TeamTasksComponent';
import DailyTasksComponent from './components/DailyTasksComponent';
import WeeklyTasksComponent from './components/WeeklyTasksComponent';
import MonthlyTasksComponent from './components/MonthlyTasksComponent';

// İkonlar
import AssignmentIcon from '@mui/icons-material/Assignment';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GroupsIcon from '@mui/icons-material/Groups'; // Ekip görevleri için yeni ikon
import RestaurantIcon from '@mui/icons-material/Restaurant'; // Mutfak ekranı için ikon

// Ana container stil tanımlaması - Yeni yapıya uygun güncelleme
const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100vh;
  background-color: #f5f5f5;
  overflow: hidden;
`;

// Content container - Sidebar ile birlikte çalışması için düzenleme
const ContentWrapper = styled(Box)<{ isSidebarCollapsed: boolean }>(({ theme, isSidebarCollapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  width: `calc(100% - ${isSidebarCollapsed ? 70 : 260}px)`,
  height: '100vh',
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: 'hidden',
}));

// Header stil tanımlaması
const StyledAppBar = styled(AppBar)`
  background-color: white;
  color: ${props => props.theme.palette.text.primary};
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
  position: static;
  z-index: 99;
`;

// Kullanıcı butonu stil tanımlaması
const UserChip = styled(Chip)(({ theme }) => ({
  height: 42,
  borderRadius: 21,
  padding: theme.spacing(0.5, 1),
  backgroundColor: '#0D47A1',
  transition: 'all 0.2s ease-in-out',
  '& .MuiChip-label': {
    color: 'white',
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    backgroundColor: 'white',
    '& .MuiChip-label': {
      color: '#0D47A1',
    },
    '& .MuiIconButton-root': {
      color: '#0D47A1',
    },
  },
}));

// İçerik alanı stil tanımlaması - Scroll için güncelleme
const ContentContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  overflowY: 'auto',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

// Yapım aşamasında mesaj stil tanımlaması
const UnderConstructionContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(8),
  textAlign: 'center',
  marginTop: theme.spacing(4),
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2),
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
}));

// Görev Sekmeleri stil tanımlaması
const TaskTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: '#f0f3f9',
  width: '100%',
  height: '42px',
  minHeight: 'unset',
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3
  },
  '& .MuiTabs-flexContainer': {
    display: 'flex',
    width: '100%',
    height: '100%',
  },
  '& .MuiTabs-scroller': {
    width: '100%',
    height: '100%',
  }
}));

const TaskTab = styled(Tab)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 'bold',
  padding: theme.spacing(0.6, 3),
  height: '42px',
  minHeight: 'unset',
  textTransform: 'none',
  minWidth: 140,
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(0.8),
    fontSize: '1.1rem',
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: '#ffffff',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    opacity: 1,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  }
}));

// Şirkete katılım kartı stil tanımlaması
const JoinCompanyCard = styled(Card)(({ theme }) => ({
  maxWidth: 700,
  margin: '0 auto',
  marginTop: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}));

// Kart header stil tanımlaması
const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 3),
  background: 'linear-gradient(to right, #0D47A1, #1976D2)',
  color: 'white',
  borderTopLeftRadius: theme.spacing(2),
  borderTopRightRadius: theme.spacing(2),
}));

// Ana buton stil tanımlaması
const PrimaryButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.2),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  width: '100%',
  backgroundColor: '#0D47A1',
  '&:hover': {
    backgroundColor: '#0A3C87',
  },
}));

// Galeri buton stil tanımlaması
const GalleryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#5E35B1',
  '&:hover': {
    backgroundColor: '#4527A0',
  },
}));

// QR Kod Modal stil tanımlaması
const QRCodeModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 320px;
  max-width: 90%;
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: 4px;
  box-shadow: 0px 4px 24px rgba(0,0,0,0.2);
  padding: ${props => props.theme.spacing(4)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Şirket adı containeri
const CompanyNameContainer = styled(Box)`
  display: flex;
  align-items: center;
  padding: 0 12px;
  margin-left: 12px;
  border-left: 2px solid #e0e0e0;
`;

// Yetki butonları için stil tanımlaması
const AuthButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  fontWeight: 'bold',
  borderRadius: 20,
  padding: theme.spacing(0.5, 2),
  textTransform: 'none',
  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
  }
}));

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  companyName?: string;
  companyId?: string;
  photoURL?: string;
}

const PersonnelTaskBoard: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [joinCompanyId, setJoinCompanyId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeComponent, setActiveComponent] = useState('dashboard');
  const { currentUser, userDetails, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [activeTaskTab, setActiveTaskTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quickSaleScreens, setQuickSaleScreens] = useState<string[]>([]);
  const [pykScreens, setPykScreens] = useState<string[]>([]);
  const [kitchenScreens, setKitchenScreens] = useState<{ id: string; name: string }[]>([]);
  
  // ✅ Kullanıcı bilgileri AuthContext cache'inden - Firebase sorgusu YOK!
  useEffect(() => {
    const setupUserData = () => {
      if (currentUser && userDetails) {
        // ✅ userDetails AuthContext'ten geliyor - Firebase sorgusu YOK!
        setUserData({
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          email: userDetails.email || '',
          companyName: userDetails.companyName || '',
          companyId: userDetails.companyId || undefined,
          photoURL: userDetails.photoURL || '',
        });
        
        // Şirket ID'sini ayrı bir state'e de kaydet
        setCompanyId(userDetails.companyId || null);
        
        console.log('-------------PERSONNEL TASK BOARD-------------');
        console.log('setupUserData - Kullanıcı bilgileri AuthContext cache\'inden alındı:');
        console.log('companyId:', userDetails.companyId);
        console.log('companyName:', userDetails.companyName);
        console.log('currentUser.uid:', currentUser.uid);
        console.log('---------------------------------------------');
        
        // Şirket ID varsa yetkileri kontrol et ve şirket bilgisini çek
        if (userDetails.companyId) {
          checkPermissions(currentUser.uid, userDetails.companyId);
          fetchCompanyInfo(userDetails.companyId, userDetails.companyName || '');
          
          // LocalStorage'a companyId bilgisini kaydedelim - TaskDetailModal için
          try {
            localStorage.setItem('userCompanyId', userDetails.companyId);
            console.log('userCompanyId localStorage\'a kaydedildi:', userDetails.companyId);
          } catch (e) {
            console.error('localStorage erişimi sırasında hata:', e);
          }
        }
        
        setLoading(false);
      } else if (currentUser && !userDetails) {
        // userDetails henüz yüklenmediyse bekle
        console.log('Waiting for userDetails to load...');
      } else {
        setLoading(false);
      }
    };
    
    setupUserData();
  }, [currentUser, userDetails]);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };
  
  const handleOpenProfileModal = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };
  
  // API isteklerini temizleyen fonksiyon
  const cleanupRequests = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  // Component unmount olduğunda tüm istekleri iptal et
  useEffect(() => {
    return () => {
      cleanupRequests();
    };
  }, []);
  
  // Firebase kimlik doğrulama hata yönetimi için
  useEffect(() => {
    const setupAuthErrorHandling = () => {
      // Google Identity Service hatalarını bastır
      if (window.console && console.error) {
        const originalError = console.error;
        console.error = (...args) => {
          // GSI_LOGGER hatalarını kontrol et ve gösterme
          if (
            args.length > 0 && 
            typeof args[0] === 'string' && 
            (args[0].includes('GSI_LOGGER') || args[0].includes('The request has been aborted'))
          ) {
            // Hata mesajını gösterme
            return;
          }
          // Diğer hataları normal şekilde göster
          originalError.apply(console, args);
        };
      }
    };

    setupAuthErrorHandling();
  }, []);

  const handleJoinCompany = async () => {
    if (!joinCompanyId.trim()) {
      setErrorMessage('Lütfen bir şirket kodu girin');
      return;
    }
    
    // Önceki isteği iptal et
    cleanupRequests();
    
    // Yeni bir AbortController oluştur
    const controller = new AbortController();
    setAbortController(controller);
    
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    
    try {
      // QR kod içeriğini parçala (şirketId/şubeId formatı için)
      const idParts = joinCompanyId.trim().split('/');
      const companyId = idParts[0];
      const branchId = idParts.length > 1 ? idParts[1] : null;
      
      console.log("Şirket Katılım İsteği - CompanyID:", companyId, "BranchID:", branchId);
      
      const response = await nodeApi.post('/api/personnel/join', {
        companyId,
        branchId
      }, {
        signal: controller.signal
      });
      
      const data = response.data;
      console.log("API yanıt:", data);
      
      setSuccessMessage(data.message || 'Şirkete başarıyla katıldınız. Sayfa yenileniyor...');
      
      // Biraz bekleyip sayfayı yenile
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.log('İstek iptal edildi');
        return;
      }
      
      console.error("Şirkete katılma hatası:", error);
      
      let errorMessage = error.response?.data?.message || error.message || 'Şirkete katılırken bir hata oluştu';
      
      if (errorMessage.includes('Personel limit') || errorMessage.includes('personel limit')) {
        errorMessage = 'Şirket personel limitine ulaşmış. Daha fazla personel eklemek için şirket yetkilinizle iletişime geçin.';
      } else if (errorMessage.includes('permission-denied')) {
        errorMessage = 'Bu işlem için yetkiniz yok. Lütfen doğru şirket kodunu kullandığınızdan emin olun.';
      } else if (error.response?.status === 404 || errorMessage.includes('bulunamadı')) {
        errorMessage = 'API endpoint bulunamadı. Lütfen destek ekibiyle iletişime geçin.';
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  // QR kod okuma için fonksiyonlar
  const handleSelectFromGallery = () => {
    cleanupRequests(); // İstekleri temizle
    fileInputRef.current?.click();
  };
  
  // Yüklenen resimden QR kodu okuma
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Önceki istekleri temizle
    cleanupRequests();
    
    setIsScanning(true);
    setErrorMessage(null);
    
    const html5QrCode = new Html5Qrcode("qr-reader");
    
    // QR kodu çözümle
    html5QrCode.scanFile(file, true)
      .then(decodedText => {
        console.log("QR Kod başarıyla okundu:", decodedText);
        setJoinCompanyId(decodedText);
        setIsScanning(false);
        setSuccessMessage('QR kod başarıyla okundu');
      })
      .catch(error => {
        console.error("QR kod tarama hatası:", error);
        setErrorMessage("QR kod tanınamadı veya geçersiz. Lütfen başka bir resim deneyin.");
        setIsScanning(false);
      });
  };

  // QR kodu paylaşma işlemi
  const handleShareQRCode = async () => {
    try {
      if (!currentUser) {
        setErrorMessage('Kullanıcı girişi yapılmamış');
        return;
      }
      
      // QR kodu canvas'a dönüştürme
      const qrCodeDiv = document.getElementById('qr-code-to-share');
      if (!qrCodeDiv) {
        setErrorMessage('QR kod oluşturulamadı');
        return;
      }
      
      // Web Share API desteği kontrolü
      if (navigator.share) {
        try {
          // Web Share API kullanarak paylaşım
          await navigator.share({
            title: 'Personel QR Kodu',
            text: `Personel ID: ${currentUser.uid}\n\nPersoneli ekleyebilirsiniz`,
          });
          setSuccessMessage('QR kod başarıyla paylaşıldı');
        } catch (error: any) {
          console.error('Paylaşım sırasında hata:', error);
          if (error.name !== 'AbortError') {
            setErrorMessage('Paylaşım sırasında bir hata oluştu');
          }
        }
      } else {
        // Paylaşım API'si desteklenmiyorsa kopyalama işlemi
        await navigator.clipboard.writeText(`Personel ID: ${currentUser.uid}\n\nPersoneli ekleyebilirsiniz`);
        setSuccessMessage('ID bilgileri panoya kopyalandı');
      }
    } catch (error) {
      console.error('QR kod paylaşılırken bir hata oluştu:', error);
      setErrorMessage('QR kod paylaşılırken bir hata oluştu');
    }
  };

  // QR kodu indirme işlemi
  const handleDownloadQRCode = () => {
    try {
      const qrCodeSvg = document.getElementById('qr-code-to-share');
      if (!qrCodeSvg) {
        setErrorMessage('QR kod bulunamadı');
        return;
      }

      // SVG'yi Data URL'ye dönüştürme
      const svgData = new XMLSerializer().serializeToString(qrCodeSvg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      // Boyutları ayarla
      canvas.width = 300;
      canvas.height = 300;
      
      img.onload = () => {
        if (!ctx) return;
        
        // Arka planı beyaz yap
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // SVG'yi çiz
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // İndirme bağlantısı oluştur
        const dataUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = dataUrl;
        downloadLink.download = 'personel-qr-kodu.png';
        downloadLink.click();
        
        setSuccessMessage('QR kod başarıyla indirildi');
      };
      
      img.onerror = () => {
        setErrorMessage('QR kod oluşturulurken bir hata oluştu');
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('QR kod indirme hatası:', error);
      setErrorMessage('QR kod indirilirken bir hata oluştu');
    }
  };

  // QR Kod Modalını aç
  const handleOpenQRModal = () => {
    setShowQRModal(true);
  };
  
  // QR Kod Modalını kapat
  const handleCloseQRModal = () => {
    setShowQRModal(false);
  };
  
  const userFullName = userData 
    ? `${userData.firstName} ${userData.lastName}` 
    : 'Kullanıcı';

  // userFullName değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (userFullName) {
      try {
        localStorage.setItem('userFullName', userFullName);
        console.log('userFullName localStorage\'a kaydedildi:', userFullName);
      } catch (e) {
        console.error('localStorage erişimi sırasında hata:', e);
      }
    }
  }, [userFullName]);


  // Sidebar tab değişikliği için handler
  const handleTabChange = (component: string) => {
    setActiveComponent(component);
  };
  
  // Sidebar daraltma için handler
  const handleSidebarCollapse = (isCollapsed: boolean) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  // Görev sekmesi değişikliği için handler
  const handleTaskTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTaskTab(newValue);
  };

  // Personelin ekran yetkilerini SADECE SQL'den (SystemUserScreenPermissions) kontrol et.
  const checkPermissions = async (userId: string, companyId: string) => {
    // Belirli screenType icin screenId->settings haritasi (SQL).
    const loadPermMap = async (screenType: string): Promise<Record<string, any>> => {
      const map: Record<string, any> = {};
      try {
        const rows = await getScreenPermissionsByType(userId, screenType);
        rows.forEach((r) => {
          if (r.isEnabled && r.screenId != null) map[String(r.screenId)] = r.settings ?? true;
        });
      } catch (e) {
        console.warn(`[PersonnelTaskBoard] SQL ${screenType} yetki okuma hatasi:`, e);
      }
      return map;
    };

    try {
      // ✅ Hızlı satış ekranları yetkilerini kontrol et
      const permittedScreens = await loadPermMap('quickSaleScreens');
      {
        const screenIds = Object.keys(permittedScreens || {});
        if (screenIds.length > 0) {
          setQuickSaleScreens(screenIds);
        }
      }
      
      // ✅ PYK ekranları yetkilerini kontrol et
      const permittedPykScreens = await loadPermMap('pykScreens');
      {
        const pykScreenIds = Object.keys(permittedPykScreens || {});
        if (pykScreenIds.length > 0) {
          setPykScreens(pykScreenIds);
        }
      }
      
      // ✅ Mutfak ekranları yetkilerini kontrol et
      const permittedKitchenScreens = await loadPermMap('kitchenScreens');
      {
        const kitchenMenuIds = Object.keys(permittedKitchenScreens || {});
        
        if (kitchenMenuIds.length > 0) {
          // ✅ QR menü bilgilerini al
          const qrMenusData = await getQrMenusMap(companyId) || {};
          
          const availableScreens = kitchenMenuIds
            .filter(menuId => {
              const permissions = permittedKitchenScreens[menuId];
              const hasQrMenu = !!qrMenusData[menuId];
              return permissions?.accessKitchenScreen && (hasQrMenu || Object.keys(qrMenusData).length === 0);
            })
            .map(menuId => ({
              id: menuId,
              name: qrMenusData[menuId]?.name || `Mutfak Ekranı ${menuId.replace('menu_', '')}`
            }));
          
          setKitchenScreens(availableScreens);
        }
      }
    } catch (error) {
      console.error("Yetki kontrolü sırasında hata:", error);
    }
  };
  
  // Hızlı satış ekranına yönlendirme
  const handleGoToQuickSale = () => {
    if (quickSaleScreens.length > 0) {
      navigate(`/hizli-satis/ekran/${quickSaleScreens[0]}`);
    }
  };
  
  // PYK ekranına yönlendirme
  const handleGoToPyk = () => {
    if (pykScreens.length > 0) {
      navigate(`/pyk-ekrani/${pykScreens[0]}`);
    }
  };
  
  // Mutfak ekranına yönlendirme
  const handleGoToKitchen = () => {
    if (kitchenScreens.length > 0) {
      navigate(`/mutfak-ekrani/${kitchenScreens[0].id}`);
    }
  };

  // Şirket bilgilerini çek (isim ve diğer detaylar) - Cloud Functions üzerinden
  const fetchCompanyInfo = async (companyId: string, fallbackName: string) => {
    try {
      console.log('-------------PERSONNEL TASK BOARD-------------');
      console.log('fetchCompanyInfo - Şirket bilgisi çekiliyor:');
      console.log('companyId:', companyId);
      console.log('fallbackName:', fallbackName);
      
      // Şirket info bilgisini SQL'den çek (GET /api/Company)
      const companyInfoData = await getCompanyInfo(companyId);
      
      let fetchedCompanyName = fallbackName;
      
      if (companyInfoData && (companyInfoData as any).name) {
        fetchedCompanyName = (companyInfoData as any).name;
        console.log('Bulunan şirket adı:', fetchedCompanyName);
        console.log('---------------------------------------------');
        setCompanyName(fetchedCompanyName);
      } else {
        // Şirket bulunamadıysa fallback ismi kullan
        console.log('Şirket bulunamadı, fallback isim kullanılıyor:', fallbackName);
        console.log('---------------------------------------------');
        setCompanyName(fallbackName);
      }
    } catch (error) {
      console.error("Şirket bilgisi alınamadı:", error);
      // Hata durumunda yine fallback ismi kullan
      setCompanyName(fallbackName);
    }
  };

  // Aktif bileşene göre içerik
  const renderContent = () => {
    switch(activeComponent) {
      case 'dashboard':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ width: '100%', borderBottom: '1px solid #e0e0e0' }}>
              <TaskTabs
                value={activeTaskTab}
                onChange={handleTaskTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="görev sekmeleri"
              >
                <TaskTab icon={<ListAltIcon />} iconPosition="start" label="Normal Görevler" />
                <TaskTab icon={<GroupsIcon />} iconPosition="start" label="Ekip Görevleri" />
                <TaskTab icon={<TodayIcon />} iconPosition="start" label="Günlük Tekrarlı" />
                <TaskTab icon={<DateRangeIcon />} iconPosition="start" label="Haftalık Tekrarlı" />
                <TaskTab icon={<CalendarMonthIcon />} iconPosition="start" label="Aylık Tekrarlı" />
              </TaskTabs>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
              {activeTaskTab === 0 && <NormalTasksComponent companyId={companyId || ''} userFullName={userFullName} />}
              {activeTaskTab === 1 && <TeamTasksComponent companyId={companyId || ''} userFullName={userFullName} />}
              {activeTaskTab === 2 && <DailyTasksComponent />}
              {activeTaskTab === 3 && <WeeklyTasksComponent />}
              {activeTaskTab === 4 && <MonthlyTasksComponent />}
            </Box>
          </Box>
        );
      case 'shifts':
        return (
          <UnderConstructionContainer>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
              Vardiyalarım
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Vardiya bilgileriniz yakında burada görüntülenecek.
            </Typography>
          </UnderConstructionContainer>
        );
      case 'notifications':
        return (
          <UnderConstructionContainer>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
              Bildirimler
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Bildirimleriniz yakında burada görüntülenecek.
            </Typography>
          </UnderConstructionContainer>
        );
      case 'settings':
        return (
          <UnderConstructionContainer>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
              Ayarlar
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Ayarlar yakında burada görüntülenecek.
            </Typography>
          </UnderConstructionContainer>
        );
      default:
        return (
          <UnderConstructionContainer>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
              Sayfa Bulunamadı
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Aradığınız sayfa mevcut değil.
            </Typography>
          </UnderConstructionContainer>
        );
    }
  };

  // Rol kontrolü - sadece employee ve courier rolleri erişebilir
  console.log('[PersonnelTaskBoard] Rol kontrolü:', {
    userDetails: userDetails,
    role: userDetails?.role,
    hasAccess: userDetails?.role === 'employee' || userDetails?.role === 'courier'
  });
  
  if (userDetails && userDetails.role !== 'employee' && userDetails.role !== 'courier') {
    return (
      <MainContainer>
        <ContentWrapper isSidebarCollapsed={false}>
          <StyledAppBar elevation={0}>
            <Toolbar>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#0D47A1', flexGrow: 1 }}>
                Erişim Engellendi
              </Typography>
              <UserChip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {userFullName}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={handleMenuOpen}
                      sx={{ ml: 0.5, p: 0, color: 'white' }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
                onClick={handleMenuOpen}
                sx={{ cursor: 'pointer' }}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { width: 200, mt: 1 }
                }}
              >
                <MenuItem onClick={handleLogout}>
                  Güvenli Çıkış
                </MenuItem>
              </Menu>
            </Toolbar>
          </StyledAppBar>
          <ContentContainer>
            <UnderConstructionContainer>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                Erişim Reddedildi
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                Bu sayfaya erişim yetkiniz bulunmamaktadır.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Rolünüz: <strong>{userDetails.role}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Bu sayfa sadece <strong>personel</strong> ve <strong>kurye</strong> rolleri için erişilebilir.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleLogout}
                sx={{ mt: 3 }}
              >
                Güvenli Çıkış
              </Button>
            </UnderConstructionContainer>
          </ContentContainer>
        </ContentWrapper>
      </MainContainer>
    );
  }

  // Bir şirkete üye olma durumunda sidebar ve içerik görüntülenir
  // Şirkete üye değilse üyelik ekranı görüntülenir
  return (
    <MainContainer>
      {companyId && (
        <PersonnelSidebar
          onTabChange={handleTabChange}
          onCollapse={handleSidebarCollapse}
        />
      )}
      
      <ContentWrapper isSidebarCollapsed={companyId ? isSidebarCollapsed : false}>
        {/* Header */}
        <StyledAppBar elevation={0}>
          <Toolbar>
            <Box sx={{ 
              display: 'flex', 
              flexGrow: 1, 
              alignItems: 'center'
            }}>
              <Typography 
                variant="h5"
                component="h1" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#0D47A1',
                }}
              >
                Görev Panosu
              </Typography>
              
              {/* Şirket ismi gösterimi */}
              {companyName && (
                <CompanyNameContainer>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: '500',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <BusinessIcon fontSize="small" sx={{ mr: 1, color: '#1976D2' }} />
                    {companyName}
                  </Typography>
                </CompanyNameContainer>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Yetki butonları */}
              {quickSaleScreens.length > 0 && (
                <AuthButton
                  variant="contained"
                  color="primary"
                  onClick={handleGoToQuickSale}
                  sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
                  title="Hızlı Satış Ekranına Git"
                >
                  Satış Ekranı
                </AuthButton>
              )}
              
              {pykScreens.length > 0 && (
                <AuthButton
                  variant="contained"
                  color="primary"
                  onClick={handleGoToPyk}
                  sx={{ bgcolor: '#FF9800', '&:hover': { bgcolor: '#F57C00' } }}
                  title="PYK Ekranına Git"
                >
                  PYK Ekranı
                </AuthButton>
              )}
              
              {kitchenScreens.length > 0 && (
                <AuthButton
                  variant="contained"
                  color="primary"
                  onClick={handleGoToKitchen}
                  sx={{ bgcolor: '#E91E63', '&:hover': { bgcolor: '#C2185B' } }}
                  title="Mutfak Ekranına Git"
                >
                  Mutfak Ekranı
                </AuthButton>
              )}
              
              <UserChip
                avatar={userData?.photoURL ? <Avatar src={userData.photoURL} /> : undefined}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {userFullName}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={handleMenuOpen}
                      sx={{ ml: 0.5, p: 0, color: 'white' }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
                onClick={handleMenuOpen}
                sx={{ cursor: 'pointer' }}
              />
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { width: 200, mt: 1 }
                }}
              >
                <MenuItem onClick={handleOpenProfileModal}>
                  Profil
                </MenuItem>
                <Divider />
                {quickSaleScreens.length > 0 && (
                  <MenuItem onClick={handleGoToQuickSale}>
                    Satış Ekranı
                  </MenuItem>
                )}
                {pykScreens.length > 0 && (
                  <MenuItem onClick={handleGoToPyk}>
                    PYK Ekranı
                  </MenuItem>
                )}
                {kitchenScreens.length > 0 && (
                  <MenuItem onClick={handleGoToKitchen}>
                    Mutfak Ekranı
                  </MenuItem>
                )}
                {(quickSaleScreens.length > 0 || pykScreens.length > 0 || kitchenScreens.length > 0) && <Divider />}
                <MenuItem onClick={handleLogout}>
                  Güvenli Çıkış
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </StyledAppBar>
        
        {/* Ana İçerik */}
        <ContentContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            companyId ? (
              // Şirkete dahil olan kullanıcı için içerik
              <>
                {/* Sekmeler header'ın hemen altında */}
                <Box sx={{ width: '100%', borderBottom: '1px solid #e0e0e0' }}>
                  <TaskTabs
                    value={activeTaskTab}
                    onChange={handleTaskTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="görev sekmeleri"
                  >
                    <TaskTab icon={<ListAltIcon />} iconPosition="start" label="Normal Görevler" />
                    <TaskTab icon={<GroupsIcon />} iconPosition="start" label="Ekip Görevleri" />
                    <TaskTab icon={<TodayIcon />} iconPosition="start" label="Günlük Tekrarlı" />
                    <TaskTab icon={<DateRangeIcon />} iconPosition="start" label="Haftalık Tekrarlı" />
                    <TaskTab icon={<CalendarMonthIcon />} iconPosition="start" label="Aylık Tekrarlı" />
                  </TaskTabs>
                </Box>
                
                {/* Seçilen sekmeye göre içerik */}
                <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto', bgcolor: '#ffffff' }}>
                  {activeComponent === 'dashboard' && (
                    <>
                      {activeTaskTab === 0 && <NormalTasksComponent companyId={companyId || ''} userFullName={userFullName} />}
                      {activeTaskTab === 1 && <TeamTasksComponent companyId={companyId || ''} userFullName={userFullName} />}
                      {activeTaskTab === 2 && <DailyTasksComponent />}
                      {activeTaskTab === 3 && <WeeklyTasksComponent />}
                      {activeTaskTab === 4 && <MonthlyTasksComponent />}
                    </>
                  )}
                  {activeComponent === 'shifts' && (
                    <UnderConstructionContainer>
                      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
                        Vardiyalarım
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Vardiya bilgileriniz yakında burada görüntülenecek.
                      </Typography>
                    </UnderConstructionContainer>
                  )}
                  {activeComponent === 'notifications' && (
                    <UnderConstructionContainer>
                      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
                        Bildirimler
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Bildirimleriniz yakında burada görüntülenecek.
                      </Typography>
                    </UnderConstructionContainer>
                  )}
                  {activeComponent === 'settings' && (
                    <UnderConstructionContainer>
                      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
                        Ayarlar
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Ayarlar yakında burada görüntülenecek.
                      </Typography>
                    </UnderConstructionContainer>
                  )}
                </Box>
              </>
            ) : (
              // Şirkete dahil olmayan kullanıcı için içerik
              <>
                {/* Personel ID Bilgisi */}
                <Card sx={{ maxWidth: 700, mx: 'auto', mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Personel ID
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D47A1', mb: 3 }}>
                      {currentUser?.uid || 'Yükleniyor...'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph sx={{ maxWidth: 450, mx: 'auto' }}>
                      LÜTFEN PERSONEL ID'NİZİ VEYA QR KODUNUZU YÖNETİCİNİZ İLE PAYLAŞINIZ
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<QrCode2Icon />}
                        onClick={handleOpenQRModal}
                        sx={{ borderRadius: 2 }}
                      >
                        QR Kodumu Göster
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {/* Şirkete Katılım Kartı */}
                <JoinCompanyCard>
                  <CardHeader>
                    <Typography variant="h5" fontWeight="bold" component="h2">
                      Şirkete Katıl
                    </Typography>
                  </CardHeader>
                  
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: '#f5f9ff', borderRadius: 2 }}>
                      <InfoIcon sx={{ color: '#0D47A1', mr: 2 }} />
                      <Typography variant="body1">
                        Herhangi bir şirkete bağlı değilsiniz. Görev panolarını görüntülemek için bir şirkete katılmanız gerekmektedir.
                      </Typography>
                    </Box>
                    
                    {errorMessage && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                        {errorMessage}
                      </Alert>
                    )}
                    
                    {successMessage && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                        {successMessage}
                      </Alert>
                    )}
                    
                    <TextField
                      fullWidth
                      label="Şirket Kodu"
                      variant="outlined"
                      value={joinCompanyId}
                      onChange={(e) => setJoinCompanyId(e.target.value)}
                      placeholder="Şirket kodunu girin"
                      sx={{ mb: 3 }}
                      InputProps={{
                        startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                    
                    <Box sx={{ mb: 3 }}>
                      <GalleryButton
                        fullWidth
                        variant="contained"
                        startIcon={<PhotoLibraryIcon />}
                        onClick={handleSelectFromGallery}
                        disabled={isLoading || isScanning}
                      >
                        {isScanning ? 'Taranıyor...' : 'Galeriden QR Kod Seç'}
                      </GalleryButton>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ px: 4, pb: 4 }}>
                    <PrimaryButton 
                      variant="contained" 
                      color="primary" 
                      fullWidth
                      disabled={isLoading || isScanning}
                      onClick={handleJoinCompany}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Şirkete Katıl'
                      )}
                    </PrimaryButton>
                  </CardActions>
                </JoinCompanyCard>
              </>
            )
          )}
        </ContentContainer>
      </ContentWrapper>
      
      {/* QR Kod Modalı */}
      <Dialog
        open={showQRModal}
        onClose={handleCloseQRModal}
        aria-labelledby="qr-code-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="qr-code-title" sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          pb: 2
        }}>
          <Typography variant="h6" component="span">Personel QR Kodu</Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseQRModal} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 3 }}>
          <Box sx={{ 
            p: 3, 
            bgcolor: 'white', 
            borderRadius: 2, 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <QRCodeSVG
              id="qr-code-to-share"
              value={currentUser?.uid || ''}
              size={220}
              bgColor={"#ffffff"}
              fgColor={"#000000"}
              level={"H"}
              includeMargin={true}
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
            Personel ID:
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 2, wordBreak: 'break-all' }}>
            {currentUser?.uid || 'Yükleniyor...'}
          </Typography>
          
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
            Bu QR kodu yöneticinize gösterdiğinizde, sizi sistemlerine kolayca ekleyebilirler.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, p: 3, borderTop: '1px solid #eee' }}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleDownloadQRCode}
          >
            İndir
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ShareIcon />}
            onClick={handleShareQRCode}
          >
            Paylaş
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Profil Modalı */}
      <PersonnelProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
      
      {/* Gizli dosya yükleme input'u */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleImageUpload}
      />
      
      {/* QR kod tarayıcısı için gizli div */}
      <div id="qr-reader" style={{ display: 'none' }}></div>
    </MainContainer>
  );
};

export default PersonnelTaskBoard; 